/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  Flex,
  Select,
  Button,
  Center,
  FormErrorMessage,
  useToast,
  Spacer,
} from '@chakra-ui/core';
import Joi from 'joi';
import _isNil from 'lodash/isNil';
import GooglePlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete';
import { OptionTypeBase, ValueType } from 'react-select';
import Bugsnag from '@bugsnag/js';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { createBusinessStyle, googleAutoCompleteStyle } from './CreateBusinessStyle';
import { Business, GoogleAutoCompleteValues } from '../BusinessType';
import {
  clearErrorMessage,
  errorFormat,
  FormattedError,
  getErrorMessage,
  hasErrorMessage,
  updateObject,
} from '../../../../utils/FormErrorUtils';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { formatPhoneNumber } from '../../../../utils/PhoneUtils';
import { NotificationTypes } from '../../../../firebase/firestore/documents/notificationTypes';
import useBusinessLocations from '../useBusinessLocations';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { PATH_SMB_BUSINESS_MANAGEMENT } from '../../../../routes/constants';
import colors from '../../../../styles/colors';

const LOCATION_ID_MAX_LENGTH = 30;

export const CreateBusinessView = (props: {
  successCallBack: (business: Business) => void;
  closeCallBack: (businessId: string) => void;
  businessData: Business;
  createNewBusiness: boolean;
}): JSX.Element => {
  const { t } = useTranslation('businessManagement');
  const location = useLocation();
  const [savingBusiness, setSavingBusiness] = useState<boolean>(false);
  const [businessData, setBusinessData] = useState<Business>(props.businessData);
  const { user } = useStoreState((s) => s.app);
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [value, setValue] = useState<ValueType<OptionTypeBase, false>>({
    label: businessData.completeAddress,
    value: { place_id: businessData.placeId },
  });
  const toast = useToast();
  const businessLocations = useBusinessLocations();
  const { createBusiness, editBusiness } = useStoreActions((actions) => actions.businessManagement);

  const enableGroupInBusinessLocation: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_GROUP_IN_BUSINESS_LOCATION];
  });
  const isSMB = useStoreState((state) => state.app.accounts?.smb);

  const countryMap: Record<string, { name: string; code: string; iso: string }> = {
    usa: { name: 'USA', code: '+1', iso: 'US' },
    'united states': { name: 'USA', code: '+1', iso: 'US' },
    'united states of america': { name: 'USA', code: '+1', iso: 'US' },
    us: { name: 'USA', code: '+1', iso: 'US' },

    canada: { name: 'Canada', code: '+1', iso: 'CA' },
    ca: { name: 'Canada', code: '+1', iso: 'CA' },

    australia: { name: 'Australia', code: '+61', iso: 'AU' },
    au: { name: 'Australia', code: '+61', iso: 'AU' },
  };

  const parse = (phoneNumber?: string, countryCode?: string): string => {
    // istanbul ignore next
    if (!phoneNumber) return '';
    const numericPhone = phoneNumber.replace(/\D/g, '');
    return `${countryCode}${numericPhone}`;
  };

  useEffect(() => {
    const initialData = { ...props.businessData };

    // Set default empty strings for controlled inputs
    initialData.webSite = initialData.webSite || '';
    initialData.state = initialData.state || '';
    initialData.cityName = initialData.cityName || '';
    initialData.zip = initialData.zip || '';
    initialData.address = initialData.address || '';
    initialData.completeAddress = initialData.completeAddress || /* istanbul ignore next */ '';
    initialData.country = countryMap[initialData.country?.toLocaleLowerCase()]?.name || 'canada';

    // istanbul ignore else
    if (initialData.phoneNumber) {
      // 1. Find the country details from the loaded data
      const countryInfo = Object.values(countryMap).find((c) => c.name.toLowerCase() === initialData.country?.toLowerCase());
      const codeToRemove = countryInfo ? countryInfo.code : /* istanbul ignore next */ '+1';

      // 2. Get all digits from the full phone number (e.g., "+15551234567" -> "15551234567")
      const allDigits = initialData.phoneNumber.replace(/\D/g, '');
      const countryCodeDigits = codeToRemove.replace(/\D/g, '');

      // 3. Strip the country code prefix to get only the local number digits
      let localNumberDigits = allDigits;
      // istanbul ignore else
      if (allDigits.startsWith(countryCodeDigits)) {
        localNumberDigits = allDigits.substring(countryCodeDigits.length);
      }

      // 4. Format the local number for display in the input field
      initialData.phoneNumber = formatPhoneNumber(localNumberDigits, countryInfo?.iso ?? /* istanbul ignore next */ 'CA');
    } else {
      // istanbul ignore next
      initialData.phoneNumber = '';
    }

    // Set the processed and cleaned data into the component's state
    setBusinessData(initialData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.businessData]);
  useEffect(() => {
    const countryInfo = Object.values(countryMap).find((c) => c.name.toLowerCase() === businessData.country?.toLowerCase());
    if (countryInfo && businessData.countryCode !== countryInfo.code) {
      setBusinessData((prev) => ({
        ...prev,
        countryCode: countryInfo.code,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessData.country]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value: targetValue } = event.target;

    if (id === 'phoneNumber') {
      const countryName = businessData.country /* istanbul ignore next */ || 'Canada';
      const countryInfo = Object.values(countryMap).find((c) => c.name.toLowerCase() === countryName.toLowerCase());
      const formattedValue = formatPhoneNumber(targetValue, countryInfo?.iso ?? /* istanbul ignore next */ 'CA');
      updateObject(businessData, id, formattedValue, false);
    } else if (id === 'country') {
      const selectedCountry = targetValue.toLowerCase();
      const country = countryMap[selectedCountry];
      /* istanbul ignore next */
      if (country) {
        businessData.country = country.name;
        businessData.countryCode = country.code;
        businessData.phoneNumber = '';
      }
    } else {
      updateObject(businessData, id, targetValue, false);
    }

    setBusinessData({ ...businessData });
    setFormErrors(clearErrorMessage(formErrors, id));
  };

  const handleChangeLocationId = (locationId: string, fieldName: string) => {
    setFormErrors(clearErrorMessage(formErrors, fieldName));

    setBusinessData((prevState) => ({
      ...prevState,
      [fieldName]: locationId,
    }));
  };

  const schema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      name: Joi.string()
        .required()
        .messages({
          'string.empty': t('createBusiness.businessNameRequired'),
        }),
      locationId: Joi.string()
        .required()
        .max(LOCATION_ID_MAX_LENGTH)
        .messages({
          'string.empty': t('createBusiness.locationIdRequired'),
          'any.required': t('createBusiness.locationIdRequired'),
          'string.max': t('createBusiness.locationIdMaxLength', { length: LOCATION_ID_MAX_LENGTH }),
        }),
      groupLvl1: Joi.string().allow(null).allow('').optional(),
      cityName: Joi.string()
        .required()
        .messages({
          'string.empty': t('createBusiness.cityNameRequired'),
        }),
      address: Joi.string()
        .required()
        .messages({
          'string.empty': t('createBusiness.addressRequired'),
        }),
      country: Joi.string()
        .required()
        .messages({
          'string.empty': t('createBusiness.countryRequired'),
        }),
      zip: Joi.string()
        .required()
        .messages({
          'string.empty': t('createBusiness.zipRequired'),
        }),
      webSite: Joi.alternatives()
        .try(Joi.string().uri(), Joi.string().domain())
        .allow(null)
        .allow('')
        .optional()
        .messages({
          'alternatives.match': t('createBusiness.websiteInvalid'),
        }),
      state: Joi.string()
        .required()
        .messages({
          'string.empty': t('createBusiness.stateRequired'),
        }),
      phoneNumber: Joi.string()
        .when('country', {
          is: 'Australia',
          then: Joi.string()
            // Accepts +61 or 0 prefix, followed by at least 9 digits
            .pattern(/^(\+?61|0)?\d{9,}$/)
            .messages({
              'string.pattern.base': t('createBusiness.phoneMin'),
            }),
          otherwise: Joi.string()
            //  For other countries, accepts +countrycode optional, then at least 10 digits
            .pattern(/^\+?\d{10,}$/)
            .messages({
              'string.pattern.base': t('createBusiness.phoneMin'),
            }),
        })
        .allow(null, '')
        .optional(),

      latitude: Joi.number()
        .required()
        .messages({
          'string.empty': t('createBusiness.latitudeRequired'),
        }),
      longitude: Joi.number()
        .required()
        .messages({
          'string.empty': t('createBusiness.longitudeRequired'),
        }),
      completeAddress: Joi.string().messages({
        'string.empty': t('createBusiness.addressRequired'),
      }),
      placeId: Joi.string().messages({
        'string.empty': t('createBusiness.placeIdRequired'),
      }),
      positions: Joi.number()
        .required()
        .messages({
          'string.empty': t('createBusiness.positionRequired'),
        }),
      id: Joi.string(),
    });

  const formValidation = (formData: Business) => {
    const { error } = schema.validate(formData);
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }

    setFormErrors([]);
    return true;
  };

  const saveBusiness = async () => {
    const countryCodeForPayload = businessData.country === 'Australia' ? businessData.countryCode : '+1';
    const hasPhone = businessData.phoneNumber && businessData.phoneNumber.trim() !== '';
    const fullPhoneNumber = hasPhone ? parse(businessData.phoneNumber, countryCodeForPayload as string) : '';
    const requestObj: Business = {
      id: businessData.id,
      name: businessData.name,
      locationId: businessData.locationId,
      groupLvl1: businessData.groupLvl1,
      cityName: businessData.cityName,
      address: businessData.address,
      country: businessData.country,
      latitude: businessData.latitude,
      longitude: businessData.longitude,
      zip: businessData.zip,
      state: businessData.state,
      phoneNumber: fullPhoneNumber,
      webSite: businessData.webSite,
      completeAddress: businessData.completeAddress,
      placeId: businessData.placeId,
      positions: 0,
    };

    const validate = formValidation(requestObj);
    if (validate && user) {
      setSavingBusiness(true);
      if (props.createNewBusiness) {
        try {
          const businessResponse = await createBusiness({ accountId: user.account, businessData: requestObj });
          businessData.id = businessResponse?.businessId;
          businessData.account = user.account;
          setSavingBusiness(false);
          props.successCallBack(businessData);
          toast({
            title: t('createBusiness.successMsg'),
            status: 'info',
            duration: 6000,
            isClosable: true,
          });
        } catch (err) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const errData: AxiosError = { ...(err as any) };
          const { response } = errData;
          setSavingBusiness(false);
          /* istanbul ignore next */
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (response?.data?.code === NotificationTypes.BUSINESS_ALREADY_EXISTS) {
            toast({
              title: t('createBusiness.businessAlreadyExists'),
              status: 'error',
              duration: 6000,
              isClosable: true,
            });
          }
          // istanbul ignore next
          if (response?.status === 409) {
            toast({
              title: response?.data?.message,
              status: 'error',
              duration: 6000,
              isClosable: true,
            });
            setFormErrors((err) => [...err, { label: 'locationId', message: response?.data?.message }]);
          }
        }
      } else {
        try {
          await editBusiness({ accountId: user.account, businessData: requestObj, businessId: businessData.id });
          props.successCallBack(businessData);
          toast({
            title: t('createBusiness.successMsgBusinessUpdate'),
            status: 'info',
            duration: 6000,
            isClosable: true,
          });
        } catch (err) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          // istanbul ignore next
          const errData: AxiosError = { ...(err as any) };
          // istanbul ignore next
          const { response } = errData;
          // istanbul ignore next
          if (response?.status === 409) {
            toast({
              title: response?.data?.message,
              status: 'error',
              duration: 6000,
              isClosable: true,
            });
            setFormErrors((err) => [...err, { label: 'locationId', message: response?.data?.message }]);
          }
        } finally {
          setSavingBusiness(false);
        }
      }
    }
  };

  const onSelectLocation = (e: GoogleAutoCompleteValues) => {
    let checkAlreadyHasLocation: boolean;
    setValue(e);
    if (props.businessData.placeId === e.value.place_id) {
      checkAlreadyHasLocation = true;
    } else
      checkAlreadyHasLocation = !!_isNil(
        businessLocations.find((businessLocation) => businessLocation.placeId === e.value.place_id),
      );

    /* istanbul ignore next */
    if (e && checkAlreadyHasLocation) {
      businessData.placeId = e.value.place_id;
      businessData.address = e.value.structured_formatting.main_text;
      const countryValue = e.value.terms[e.value.terms.length - 1].value.toLowerCase();
      const countryInfo = countryMap[countryValue];
      if (countryInfo) {
        businessData.country = countryInfo.name;
        businessData.countryCode = countryInfo.code;
      }
      geocodeByPlaceId(e.value.place_id)
        .then((results) => {
          const city: google.maps.GeocoderAddressComponent[] = results[0].address_components.filter((address_component) =>
            ['locality', 'colloquial_area'].some((word) => {
              /* eslint no-bitwise: [2, { allow: ["~"] }] */
              return ~address_component.types.indexOf(word);
            }),
          );
          const state: google.maps.GeocoderAddressComponent[] = results[0].address_components.filter((address_component) =>
            ['administrative_area_level_1'].some((word) => {
              return ~address_component.types.indexOf(word);
            }),
          );
          const postal: google.maps.GeocoderAddressComponent[] = results[0].address_components.filter((address_component) =>
            ['postal_code'].some((word) => {
              return ~address_component.types.indexOf(word);
            }),
          );

          const completeAddress = results[0].formatted_address;

          /* istanbul ignore else */
          if (city) {
            businessData.cityName = city[0].short_name;
          }
          /* istanbul ignore else */
          if (postal) {
            businessData.zip = postal[0].short_name;
          }
          /* istanbul ignore else */
          if (state) {
            businessData.state = state[0].short_name;
          }
          /* istanbul ignore else */
          if (completeAddress) {
            businessData.completeAddress = completeAddress;
          }
          return getLatLng(results[0]);
        })
        .then(({ lat, lng }) => {
          businessData.latitude = lat;
          businessData.longitude = lng;
          setBusinessData({ ...businessData });
        })
        .catch((error: any) => {
          Bugsnag.notify(error);
        });
      setBusinessData({ ...businessData });
    } else {
      toast({
        title: t('createBusiness.businessAlreadyExists'),
        status: 'error',
        isClosable: true,
      });
      setValue({
        label: '',
        value: {
          place_id: '',
        },
      });
    }
  };
  const countryCode = businessData.countryCode || '+1';

  return (
    <Box css={createBusinessStyle} data-testid="createBusinessComponent">
      <Box backgroundColor={colors.white} borderRadius={5} mb={6}>
        <Box px={6} py={3}>
          <Flex>
            {props.createNewBusiness ? (
              <Box>
                <Heading as="h3" fontSize="md" lineHeight="1.3" mb={1}>
                  {t('createBusiness.smbBusinessCreation')}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  {t('createBusiness.smbBusinessCreationDetail')}
                </Text>
              </Box>
            ) : (
              ''
            )}
            <Spacer />
            <Center>
              {location.pathname !== PATH_SMB_BUSINESS_MANAGEMENT && (
                <Button
                  colorScheme="blue"
                  data-testid="createBusinessCloseBtn"
                  className="closeIcon"
                  data={businessData.id}
                  onClick={() => props.closeCallBack(businessData.id)}
                >
                  {t('createBusiness.viewBusinessList')}
                </Button>
              )}
            </Center>
          </Flex>
        </Box>

        <Divider borderColor={colors.gray[50]} />
        <Flex px={6} py={3}>
          <Box w={!isSMB ? '50%' : '100%'} mr={6}>
            <FormControl isInvalid={hasErrorMessage(formErrors, 'name')} isRequired>
              <FormLabel htmlFor="name">{t('createBusiness.businessName')}</FormLabel>
              <Input
                data-testid="businessNameField"
                variant="filled"
                id="name"
                type="text"
                value={businessData.name}
                onChange={handleChange}
              />
              <FormErrorMessage>{getErrorMessage(formErrors, 'name')}</FormErrorMessage>
            </FormControl>
          </Box>
          {!isSMB && (
            <Box w={enableGroupInBusinessLocation ? '25%' : '50%'} mr={enableGroupInBusinessLocation ? 6 : 0}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'locationId')} isRequired>
                <FormLabel htmlFor="locationId">{t('createBusiness.businessId')}</FormLabel>
                <Input
                  data-testid="businessIdField"
                  variant="filled"
                  id="locationId"
                  type="text"
                  value={businessData.locationId}
                  maxLength={LOCATION_ID_MAX_LENGTH}
                  onChange={(e) => handleChangeLocationId(e.target.value, e.target.id)}
                />

                <FormErrorMessage>{getErrorMessage(formErrors, 'locationId')}</FormErrorMessage>
              </FormControl>
            </Box>
          )}

          {!isSMB && enableGroupInBusinessLocation && (
            <Box w="25%">
              <FormControl>
                <FormLabel htmlFor="locationId">{t('createBusiness.group')}</FormLabel>
                <Input
                  data-testid="groupLvl1Field"
                  variant="filled"
                  id="groupLvl1"
                  type="text"
                  maxLength={80}
                  value={businessData.groupLvl1}
                  onChange={(e) => handleChangeLocationId(e.target.value, e.target.id)}
                />
              </FormControl>
            </Box>
          )}
        </Flex>
        <Box px={6} py={3}>
          <FormControl isInvalid={hasErrorMessage(formErrors, 'address')} isRequired>
            <FormLabel htmlFor="jobLocation">{t('createBusiness.address')}</FormLabel>
            <Box css={googleAutoCompleteStyle}>
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                debounce={1000}
                minLengthAutocomplete={3}
                data-testid="googleAutoComplete"
                selectProps={{
                  placeholder: t('createBusiness.address'),
                  value,
                  onChange: (e) => {
                    onSelectLocation(e as GoogleAutoCompleteValues);
                  },
                }}
              />
            </Box>
            <FormErrorMessage>{getErrorMessage(formErrors, 'address')}</FormErrorMessage>
          </FormControl>
        </Box>
        <Box px={6} py={3}>
          <Flex>
            <Box w="50%" mr={6}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'cityName')} isRequired>
                <FormLabel htmlFor="cityName">{t('createBusiness.city')}</FormLabel>
                <Input
                  data-testid="businessCityField"
                  variant="filled"
                  id="cityName"
                  type="text"
                  onChange={handleChange}
                  value={businessData.cityName}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'cityName')}</FormErrorMessage>
              </FormControl>
            </Box>
            <Box w="50%">
              <FormControl isInvalid={hasErrorMessage(formErrors, 'state')} isRequired>
                <FormLabel htmlFor="state">{t('createBusiness.state')}</FormLabel>
                <Input
                  data-testid="businessStateField"
                  variant="filled"
                  id="state"
                  type="text"
                  onChange={handleChange}
                  value={businessData.state}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'state')}</FormErrorMessage>
              </FormControl>
            </Box>
          </Flex>
        </Box>
        <Box px={6} py={3}>
          <Flex>
            <Box w="50%" mr={6}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'zip')} isRequired>
                <FormLabel htmlFor="zip">{t('createBusiness.zip')}</FormLabel>
                <Input
                  data-testid="businessZipField"
                  variant="filled"
                  id="zip"
                  type="text"
                  value={businessData.zip}
                  onChange={handleChange}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'zip')}</FormErrorMessage>
              </FormControl>
            </Box>
            <Box w="50%">
              <FormControl isInvalid={hasErrorMessage(formErrors, 'country')} isRequired>
                <FormLabel htmlFor="country">{t('createBusiness.country')}</FormLabel>
                <Select
                  data-testid="businessCountryField"
                  className="filled"
                  id="country"
                  onChange={handleChange}
                  value={businessData.country}
                >
                  <option value="Australia">{t('createBusiness.australia')}</option>
                  <option value="Canada">{t('createBusiness.canada')}</option>
                  <option value="USA">{t('createBusiness.usa')}</option>
                </Select>
                <FormErrorMessage>{getErrorMessage(formErrors, 'country')}</FormErrorMessage>
              </FormControl>
            </Box>
          </Flex>
        </Box>
        <Box px={6} py={3}>
          <iframe
            src={`https://maps.google.com/maps?q=${businessData.completeAddress}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
            width="100%"
            height="450"
            frameBorder="0"
            title={businessData.name}
            style={{ border: 0 }}
            aria-hidden="false"
          />
        </Box>
        <Box px={6} py={3}>
          <Flex mb={8}>
            <Box w="50%" mr={6}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'webSite')}>
                <FormLabel htmlFor="webSite">{t('createBusiness.url')}</FormLabel>
                <Input
                  data-testid="businessWebsiteField"
                  variant="filled"
                  id="webSite"
                  type="text"
                  value={businessData.webSite}
                  onChange={handleChange}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'webSite')}</FormErrorMessage>
              </FormControl>
            </Box>
            <Box w="50%" mr={6}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'phoneNumber')}>
                <FormLabel htmlFor="phoneNumber">{t('createBusiness.phone')}</FormLabel>
                <Flex w="100%">
                  <Box w="15%" borderRight={`2px solid ${colors.gray[100]}`}>
                    <Input
                      data-testid="businessCountryCodeField"
                      variant="filled"
                      id="countryCode"
                      type="tel"
                      value={countryCode}
                      borderRightRadius="0"
                      borderTopRightRadius="0"
                      borderBottomRightRadius="0"
                      textAlign="center"
                      isDisabled
                      fontWeight="bold"
                      color={colors.gray[700]}
                      backgroundColor={colors.gray[50]}
                      _disabled={{
                        opacity: 0.5,
                        color: colors.gray[700],
                        cursor: 'not-allowed',
                      }}
                    />
                  </Box>
                  <Box w="85%">
                    <Input
                      data-testid="businessPhoneNumberField"
                      variant="filled"
                      id="phoneNumber"
                      type="tel"
                      maxLength={businessData.country === 'Australia' ? 10 : 14}
                      borderLeftRadius="0"
                      borderTopLeftRadius="0"
                      borderBottomLeftRadius="0"
                      value={businessData.phoneNumber}
                      onChange={handleChange}
                    />
                  </Box>
                </Flex>

                <FormErrorMessage>{getErrorMessage(formErrors, 'phoneNumber')}</FormErrorMessage>
              </FormControl>
            </Box>
          </Flex>
        </Box>
      </Box>
      <Box d="flex" alignItems="center" justifyContent="flex-end" mb={isSMB ? 4 : 0}>
        <Button
          data-testid="CreateBusinessBtn"
          colorScheme="blue"
          isLoading={savingBusiness}
          style={{
            position: 'fixed',
            bottom: '50px',
            right: '50px',
          }}
          onClick={saveBusiness}
        >
          {props.createNewBusiness ? t('createBusiness.create') : t('createBusiness.update')}
        </Button>
      </Box>
    </Box>
  );
};
